import React, { useState, useCallback, useEffect, useRef } from "react";
import PaymentForm, { PAYMENT_DETAILS_DEFAULT, validatePaymentDetails } from "../../../Common/PaymentForm/PaymentForm";
import { Link } from "react-router-dom";
import { loginUser, signupUser, verifyOtp, googleSignIn, getRedeemableBalance, getTotalWalletBalance, addShopOrder, getShopOrders, getNonRedeemableBalance, hasEarnedRedeemable } from "../../Users/Auth.js";
import { useAuth } from "../../../../context/AuthContext";
import userService from "../../../../services/userService";
import shoppingService from "../../../../services/shoppingService";
import "./ShoppingMart.css";
import {
  SHOP_CONFIG,
  SHOP_BANNER_OFFERS,
  SHOP_CATEGORIES,
  SHOP_FILTERS,
  SHOP_PAYMENT_METHODS,
  CONVENIENCE_FEES,
  SHOP_INITIAL_ORDERS,
  TIP_PRESETS,
  ADDRESS_TYPES,
  fmt,
  calcDiscount,
  genOrderId,
  genTrackingId,
  downloadReceipt,
  getBadgeClass,
  POINTS_RATE,
  fetchSavedAddresses,
  fetchSaveAddressBook,
  getSavedAddresses,
  saveAddressBook,
} from "./ShoppingConstants";

/* ============================================================
   STARSs
============================================================ */
function Stars({ rating }) {
  return (
    <div className="shop-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={`shop-star ${
            rating >= i
              ? "shop-star-full"
              : rating >= i - 0.5
              ? "shop-star-half"
              : "shop-star-empty"
          }`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

/* ============================================================
   TOAST
============================================================ */
function Toast({ message, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 2800);
    return () => clearTimeout(t);
  }, [onClose]);
  return <div className="shop-toast">{message}</div>;
}

/* ============================================================
   TICKER
============================================================ */
function Ticker() {
  return (
    <div className="shop-ticker">
      <div className="shop-ticker-inner">
        {[...SHOP_BANNER_OFFERS, ...SHOP_BANNER_OFFERS].map((o, i) => (
          <span key={i} className="shop-ticker-item">
            <strong>{o.highlight}:</strong> {o.text}
            <span className="shop-ticker-sep"> | </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   AUTH MODAL — Sign In · Sign Up · Google
============================================================ */
function AuthModal({ onClose, onLogin }) {
  const [tab, setTab]       = useState("signin");
  const [siForm, setSiForm] = useState({ email: "", password: "" });
  const [suForm, setSuForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [siErr,  setSiErr]  = useState("");
  const [suErr,  setSuErr]  = useState("");
  const [loading, setLoading] = useState(false);
  const setSi = (e) => setSiForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const setSu = (e) => setSuForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  function doSignIn() {
    setSiErr("");
    if (!siForm.email || !siForm.password) { setSiErr("Please fill all fields."); return; }
    setLoading(true);
    const r = loginUser({ email: siForm.email, password: siForm.password });
    setLoading(false);
    if (!r.success) { setSiErr(r.error); return; }
    onLogin(r.user); onClose();
  }

  function doSignUp() {
    setSuErr("");
    if (!suForm.name || !suForm.email || !suForm.password || !suForm.confirm) { setSuErr("All fields are required."); return; }
    if (suForm.password !== suForm.confirm) { setSuErr("Passwords do not match."); return; }
    if (suForm.password.length < 6) { setSuErr("Password must be at least 6 characters."); return; }
    setLoading(true);
    const r = signupUser({ name: suForm.name, email: suForm.email, password: suForm.password, role: "individual" });
    if (!r.success) { setSuErr(r.error); setLoading(false); return; }
    verifyOtp(suForm.email, r.otp);
    const lr = loginUser({ email: suForm.email, password: suForm.password });
    setLoading(false);
    if (!lr.success) { setSuErr(lr.error); return; }
    onLogin(lr.user); onClose();
  }

  function doGoogle() {
    setLoading(true);
    setTimeout(() => {
      const r = googleSignIn("individual");
      setLoading(false);
      if (r.success) { onLogin(r.user); onClose(); }
    }, 600);
  }

  return (
    <div className="shop-modal-overlay">
      <div className="shop-auth-modal">
        <button className="shop-auth-close" onClick={onClose}>✕</button>
       
        <p className="shop-auth-tagline">Access your points, orders &amp; wishlist</p>

        <button className="shop-google-btn" onClick={doGoogle} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.58-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          {loading ? "Please wait…" : "Continue with Google"}
        </button>

        <div className="shop-auth-divider"><span>or</span></div>

        <div className="shop-auth-tabs">
          <button className={`shop-auth-tab${tab === "signin" ? " shop-active" : ""}`} onClick={() => { setTab("signin"); setSiErr(""); }}>Sign In</button>
          <button className={`shop-auth-tab${tab === "signup" ? " shop-active" : ""}`} onClick={() => { setTab("signup"); setSuErr(""); }}>Sign Up</button>
        </div>

        {tab === "signin" && (
          <div className="shop-auth-body">
            <div className="shop-input-group">
              <label className="shop-input-label">Email</label>
              <input className="shop-input" name="email" value={siForm.email} onChange={setSi} placeholder="you@email.com" type="email" onKeyDown={(e) => e.key === "Enter" && doSignIn()} />
            </div>
            <div className="shop-input-group">
              <label className="shop-input-label">Password</label>
              <input className="shop-input" name="password" value={siForm.password} onChange={setSi} placeholder="••••••••" type="password" onKeyDown={(e) => e.key === "Enter" && doSignIn()} />
            </div>
            {siErr && <p className="shop-auth-err">⚠ {siErr}</p>}
            <button className="shop-btn-solid" onClick={doSignIn} disabled={loading || !siForm.email || !siForm.password}>
              {loading ? "Signing in…" : "Sign In "}
            </button>
            <p className="shop-auth-hint">No account? <button className="shop-auth-link" onClick={() => setTab("signup")}>Sign up free</button></p>
          </div>
        )}

        {tab === "signup" && (
          <div className="shop-auth-body">
            <div className="shop-input-group">
              <label className="shop-input-label">Full Name</label>
              <input className="shop-input" name="name" value={suForm.name} onChange={setSu} placeholder="Your name" />
            </div>
            <div className="shop-input-group">
              <label className="shop-input-label">Email</label>
              <input className="shop-input" name="email" value={suForm.email} onChange={setSu} placeholder="you@email.com" type="email" />
            </div>
            <div className="shop-input-group">
              <label className="shop-input-label">Password</label>
              <input className="shop-input" name="password" value={suForm.password} onChange={setSu} placeholder="Min 6 characters" type="password" />
            </div>
            <div className="shop-input-group">
              <label className="shop-input-label">Confirm Password</label>
              <input className="shop-input" name="confirm" value={suForm.confirm} onChange={setSu} placeholder="Repeat password" type="password" onKeyDown={(e) => e.key === "Enter" && doSignUp()} />
            </div>
            {suErr && <p className="shop-auth-err">⚠ {suErr}</p>}
            <button className="shop-btn-solid" onClick={doSignUp} disabled={loading || !suForm.name || !suForm.email || !suForm.password}>
              {loading ? "Creating account…" : "Create Account "}
            </button>
            <p className="shop-auth-hint">Already have an account? <button className="shop-auth-link" onClick={() => setTab("signin")}>Sign in</button></p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   ADDRESS BOOK COMPONENT — reusable in Profile & PaymentModal
============================================================ */
function AddressBook({ email, onSelect, selectedId, compact = false }) {
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const emptyAddr = { type: "home", label: "", name: "", phone: "", email: "", address: "", city: "", pin: "" };
  const [form, setForm] = useState(emptyAddr);
  const setF = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  useEffect(() => {
    fetchSavedAddresses(email).then(setAddresses).catch(() => setAddresses(getSavedAddresses(email)));
  }, [email]);

  function openNew() { setForm(emptyAddr); setEditIdx(null); setShowForm(true); }
  function openEdit(idx) { setForm({ ...addresses[idx] }); setEditIdx(idx); setShowForm(true); }

  function handleSave() {
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) return;
    const updated = editIdx !== null
      ? addresses.map((a, i) => (i === editIdx ? { ...form, id: a.id } : a))
      : [...addresses, { ...form, id: Date.now().toString() }];
    setAddresses(updated);
    fetchSaveAddressBook(email, updated);
    setShowForm(false);
    setEditIdx(null);
  }

  function handleDelete(idx) {
    const updated = addresses.filter((_, i) => i !== idx);
    setAddresses(updated);
    fetchSaveAddressBook(email, updated);
  }

  const typeInfo = (id) => ADDRESS_TYPES.find((t) => t.id === id) || ADDRESS_TYPES[2];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {!compact && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="shop-profile-form-title" style={{ margin: 0 }}>Saved Addresses</div>
          <button className="shop-btn-sm-blue" onClick={openNew}>+ Add New</button>
        </div>
      )}

      {addresses.length === 0 && !showForm && (
        <div style={{ padding: "16px 0", textAlign: "center", color: "var(--shop-text-muted)", fontSize: 13 }}>
          No saved addresses yet.{" "}
          <button className="shop-auth-link" onClick={openNew}>Add one</button>
        </div>
      )}

      {addresses.map((addr, idx) => {
        const ti = typeInfo(addr.type);
        const isSelected = selectedId === addr.id;
        return (
          <div
            key={addr.id}
            onClick={() => onSelect && onSelect(addr)}
            style={{
              border: `2px solid ${isSelected ? "var(--shop-heading)" : "var(--shop-border)"}`,
              borderRadius: "var(--shop-r-md)",
              padding: "12px 14px",
              background: isSelected ? "var(--shop-navy-pale)" : "var(--shop-white)",
              cursor: onSelect ? "pointer" : "default",
              transition: "all var(--shop-t)",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                {onSelect && (
                  <input type="radio" checked={isSelected} onChange={() => onSelect(addr)} style={{ accentColor: "var(--shop-heading)" }} />
                )}
                <span style={{ fontSize: 16 }}>{ti.icon}</span>
                <span style={{ fontFamily: "var(--shop-f-head)", fontWeight: 700, fontSize: 13, color: "var(--shop-heading)", textTransform: "capitalize" }}>
                  {addr.type}{addr.label ? ` — ${addr.label}` : ""}
                </span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="shop-btn-sm-outline" style={{ padding: "3px 10px", fontSize: 11 }} onClick={(e) => { e.stopPropagation(); openEdit(idx); }}>Edit</button>
                <button className="shop-btn-sm-outline" style={{ padding: "3px 10px", fontSize: 11, color: "var(--shop-red)", borderColor: "rgba(192,57,43,0.3)" }} onClick={(e) => { e.stopPropagation(); handleDelete(idx); }}>Delete</button>
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: "var(--shop-text)", fontWeight: 600 }}>{addr.name} · {addr.phone}</div>
            {addr.email && <div style={{ fontSize: 11.5, color: "var(--shop-text-muted)" }}>{addr.email}</div>}
            <div style={{ fontSize: 12, color: "var(--shop-text-mid)" }}>
              {[addr.address, addr.city, addr.pin].filter(Boolean).join(", ")}
            </div>
          </div>
        );
      })}

      {compact && (
        <button className="shop-btn-sm-blue" onClick={openNew} style={{ alignSelf: "flex-start" }}>+ Add New Address</button>
      )}

      {showForm && (
        <div style={{ border: "1.5px solid var(--shop-border)", borderRadius: "var(--shop-r-md)", padding: "16px", background: "var(--shop-bg)", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontFamily: "var(--shop-f-head)", fontWeight: 700, fontSize: 13.5, color: "var(--shop-heading)" }}>
            {editIdx !== null ? "Edit Address" : "Add New Address"}
          </div>

          {/* Address Type selector */}
          <div className="shop-input-group">
            <label className="shop-input-label">Address Type *</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {ADDRESS_TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, type: t.id }))}
                  style={{
                    padding: "6px 14px",
                    borderRadius: "50px",
                    border: `1.5px solid ${form.type === t.id ? "var(--shop-heading)" : "var(--shop-border)"}`,
                    background: form.type === t.id ? "var(--shop-heading)" : "var(--shop-white)",
                    color: form.type === t.id ? "#fff" : "var(--shop-text-mid)",
                    fontFamily: "var(--shop-f-body)",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all var(--shop-t)",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="shop-input-group">
            <label className="shop-input-label">Address Label (optional)</label>
            <input
              className="shop-input"
              name="label"
              value={form.label}
              onChange={setF}
              placeholder="e.g. 'Mom's Place', 'Office Tower B'"
            />
          </div>

          {/* Name & Phone — required */}
          <div className="shop-input-row">
            <div className="shop-input-group">
              <label className="shop-input-label">Full Name *</label>
              <input className="shop-input" name="name" value={form.name} onChange={setF} placeholder="Recipient name" />
            </div>
            <div className="shop-input-group">
              <label className="shop-input-label">Phone Number *</label>
              <input className="shop-input" name="phone" value={form.phone} onChange={setF} placeholder="+91 98765 43210" type="tel" />
            </div>
          </div>

          {/* Email — optional */}
          <div className="shop-input-group">
            <label className="shop-input-label">Email (optional)</label>
            <input className="shop-input" name="email" value={form.email} onChange={setF} placeholder="recipient@email.com" type="email" />
          </div>

          <div className="shop-input-group">
            <label className="shop-input-label">Delivery Address *</label>
            <textarea className="shop-input shop-textarea" name="address" value={form.address} onChange={setF} placeholder="House / Flat no, Street, Area" rows={2} />
          </div>

          <div className="shop-input-row">
            <div className="shop-input-group">
              <label className="shop-input-label">City</label>
              <input className="shop-input" name="city" value={form.city} onChange={setF} placeholder="City" />
            </div>
            <div className="shop-input-group">
              <label className="shop-input-label">PIN Code</label>
              <input className="shop-input" name="pin" value={form.pin} onChange={setF} placeholder="110001" />
            </div>
          </div>

          {(!form.name.trim() || !form.phone.trim() || !form.address.trim()) && (
            <p style={{ fontSize: 11.5, color: "var(--shop-red)", fontWeight: 600 }}>⚠ Name, phone and address are required.</p>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="shop-btn-solid"
              style={{ flex: 2 }}
              onClick={handleSave}
              disabled={!form.name.trim() || !form.phone.trim() || !form.address.trim()}
            >
              {editIdx !== null ? "Save Changes" : "Save Address"}
            </button>
            <button className="shop-btn-sm-outline" style={{ flex: 1 }} onClick={() => { setShowForm(false); setEditIdx(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   CONSENT MODAL — shown before payment proceeds
============================================================ */
function ConsentModal({ onAgree, onClose }) {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="shop-modal-overlay">
      <div className="shop-modal shop-modal-wide">
        <div className="shop-modal-head">
          <span className="shop-modal-title">📋 Consent &amp; Agreement</span>
          <button className="shop-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="shop-modal-body">
          <div style={{
            background: "var(--shop-navy-pale)",
            border: "1.5px solid var(--shop-navy-soft)",
            borderRadius: "var(--shop-r-md)",
            padding: "16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}>
            <div style={{ fontFamily: "var(--shop-f-head)", fontSize: 13.5, fontWeight: 800, color: "var(--shop-heading)", marginBottom: 2 }}>
              By proceeding with this purchase, you confirm that:
            </div>
            {[
              "You understand that the product can be purchased using a combination of earned reward points and/or monetary payment.",
              "If you choose to redeem your points, the corresponding number of points will be permanently deducted from your account at the time of purchase.",
              "In cases where points fully cover the purchase, no monetary transaction will be required.",
              "All purchases made are final and subject to the platform's refund and cancellation policy.",
              "You agree that the use of points and/or payment is authorized by you and cannot be reversed once the order is confirmed.",
              "You accept the terms and conditions of this platform.",
            ].map((text, i) => (
              <div key={i} style={{ display: "flex", gap: 10, fontSize: 12.5, color: "var(--shop-text-mid)", lineHeight: 1.6 }}>
                <span style={{ color: "var(--shop-green)", fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
                <span>{text}</span>
              </div>
            ))}
          </div>

          {/* Checkbox */}
          <label style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            padding: "14px 16px",
            border: `2px solid ${agreed ? "var(--shop-heading)" : "var(--shop-border)"}`,
            borderRadius: "var(--shop-r-md)",
            background: agreed ? "var(--shop-navy-pale)" : "var(--shop-white)",
            cursor: "pointer",
            transition: "all var(--shop-t)",
          }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ accentColor: "var(--shop-heading)", width: 18, height: 18, flexShrink: 0, marginTop: 1 }}
            />
            <span style={{ fontSize: 13, fontWeight: 600, color: agreed ? "var(--shop-heading)" : "var(--shop-text-mid)", lineHeight: 1.5 }}>
              I have read and agree to the terms and conditions, and I authorize the use of my reward points and/or payment for this purchase.
            </span>
          </label>

          <button
            className="shop-btn-solid"
            style={{ padding: 14, fontSize: 14.5, opacity: agreed ? 1 : 0.5 }}
            disabled={!agreed}
            onClick={onAgree}
          >
            I Agree &amp; Proceed to Payment 
          </button>

          <button className="shop-continue-link" onClick={onClose} style={{ textAlign: "center" }}>
            Cancel &amp; go back
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SHOP PROFILE — full page (replaces UserDetailsModal overlay)
============================================================ */
function ShopProfilePage({ user, userPoints, redeemableBalance, hasEarnedRedeemable, onBack, onLogout }) {
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", city: "", pin: "", address: "" });
  const [savedMsg, setSavedMsg] = useState(false);
  const initial = (user?.name || "U")[0].toUpperCase();
  const setF = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  function handleSave() {
    if (form.name) {
      userService.updateProfile({ name: form.name, phone: form.phone }).catch(() => {});
    }
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2000);
  }

  return (
    <div className="shop-fullpage">
      <div className="shop-fullpage-head">
        <button className="shop-back-btn" onClick={onBack}>Back to Shop</button>
        <div />
      </div>
      <div className="shop-profile-page">
        <div className="shop-profile-hero">
          <div className="shop-profile-avatar">{initial}</div>
          <div className="shop-profile-hero-info">
            <div className="shop-profile-uname">{user?.name}</div>
            <div className="shop-profile-uemail">{user?.email}</div>
            <span className="shop-profile-role-badge">{user?.role || "individual"}</span>
          </div>
        </div>
         {/* ── Saved Address Book in Profile ── */}
        <div className="shop-profile-form-card">
          <AddressBook email={user?.email} />
        </div>
      
        <div className="shop-profile-form-card">
          <div className="shop-profile-form-title">Account Details</div>
          <div className="shop-profile-fields">
            <div className="shop-input-group">
              <label className="shop-input-label">Full Name</label>
              <input className="shop-input" name="name" value={form.name} onChange={setF} placeholder="Your name" />
            </div>
            <div className="shop-input-group">
              <label className="shop-input-label">Phone Number</label>
              <input className="shop-input" name="phone" value={form.phone} onChange={setF} placeholder="+91 98765 43210" type="tel" />
            </div>
            <div className="shop-input-group">
              <label className="shop-input-label">Email Address</label>
              <input className="shop-input" value={user?.email || ""} readOnly style={{ opacity: 0.6, cursor: "not-allowed" }} />
            </div>
            <div className="shop-input-group">
              <label className="shop-input-label">Delivery Address</label>
              <textarea className="shop-input shop-textarea" name="address" value={form.address} onChange={setF} placeholder="House / Flat no, Street, Area" rows={2} />
            </div>
            <div className="shop-input-row">
              <div className="shop-input-group">
                <label className="shop-input-label">City</label>
                <input className="shop-input" name="city" value={form.city} onChange={setF} placeholder="City" />
              </div>
              <div className="shop-input-group">
                <label className="shop-input-label">PIN Code</label>
                <input className="shop-input" name="pin" value={form.pin} onChange={setF} placeholder="110001" />
              </div>
            </div>
          </div>
          <div className="shop-profile-form-actions">
            <button className="shop-btn-solid" onClick={handleSave}>{savedMsg ? "✓ Saved!" : "Save Changes"}</button>
            {hasEarnedRedeemable && (
              <a href="/profile" className="shop-details-profile-btn">Full Profile </a>
            )}
          </div>
        </div>

       

        <button className="shop-profile-signout-btn" onClick={onLogout}>Sign Out</button>
      </div>
    </div>
  );
}

/* ============================================================
   BUY WITH POINTS MODAL
============================================================ */
function BuyWithPointsModal({ product, qty, userPoints, onConfirm, onClose }) {
  const required      = product.pointsPrice * qty;
  const rupeesNeeded  = required / POINTS_RATE;
  const canAfford     = userPoints >= rupeesNeeded;

  return (
    <div className="shop-modal-overlay">
      <div className="shop-modal">
        <div className="shop-modal-head">
          <span className="shop-modal-title">⭐ Buy with Points</span>
          <button className="shop-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="shop-modal-body">
          <div className="shop-pts-modal-info">
            <div className="shop-pts-modal-row">
              <span>Product</span>
              <strong style={{ maxWidth: 220, textAlign: "right", fontSize: 12, lineHeight: 1.4 }}>
                {product.shortName}
              </strong>
            </div>
            <div className="shop-pts-modal-row">
              <span>Quantity</span>
              <strong>× {qty}</strong>
            </div>
            <div className="shop-pts-modal-row">
              <span>Cost per item</span>
              <strong>{product.pointsPrice.toLocaleString("en-IN")} pts</strong>
            </div>
            <div className="shop-pts-modal-row shop-pts-modal-total">
              <span>Total Points Required</span>
              <span>{required.toLocaleString("en-IN")} pts ≈ ₹{rupeesNeeded.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
            <span className="shop-pts-modal-avail">
              Your balance:{" "}
              <strong style={{ color: "var(--shop-heading)" }}>
                {userPoints.toLocaleString("en-IN")} pts
              </strong>
            </span>
            {!canAfford && <span className="shop-pts-insuff">⚠ Insufficient balance</span>}
          </div>

          {canAfford ? (
            <>
              <p style={{ fontSize: 12.5, color: "var(--shop-text-muted)", lineHeight: 1.6 }}>
                Your order will be placed and{" "}
                <strong>{required.toLocaleString("en-IN")} pts</strong> will be deducted from your points balance ({POINTS_RATE} pts = ₹1).
              </p>
              <button
                className="shop-btn-glass-green"
                style={{ padding: 13, fontSize: 14 }}
                onClick={() => onConfirm(required)}
              >
                ⭐ Confirm — Use {required.toLocaleString("en-IN")} pts
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12.5, color: "var(--shop-text-muted)", lineHeight: 1.6 }}>
                You need{" "}
                <strong>{(rupeesNeeded * POINTS_RATE - userPoints * POINTS_RATE).toLocaleString("en-IN")} pts more</strong>{" "}
                points balance to buy this product.
              </p>
              <button className="shop-btn-sm-outline" style={{ width: "100%", padding: 12 }} onClick={onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PRODUCT CARD
============================================================ */
function ProductCard({ product, onAddCart, onViewDetail, onWishlist, isWishlisted, onBuyWithPoints }) {
  const [qty, setQty] = useState(1);
  const pct = calcDiscount(product.mrp, product.price);

  return (
    <div
      className={`shop-card${!product.inStock ? " shop-oos" : ""}`}
      onClick={(e) => { if (e.target.closest("button")) return; onViewDetail(product); }}
      data-aos="fade-up"
    >
      <div className="shop-card-img-wrap">
        <img className="shop-card-img" src={product.image} alt={product.shortName} loading="lazy" />
        {product.badge && (
          <span className={`shop-card-badge ${getBadgeClass(product.badge)}`}>{product.badge}</span>
        )}
        {!product.inStock && (
          <div className="shop-oos-overlay">
            <span className="shop-oos-text">Out of Stock</span>
          </div>
        )}
        <button className={`shop-wish-btn${isWishlisted ? " shop-wished" : ""}`} onClick={() => onWishlist(product.id)}>
          {isWishlisted ? "♥" : "♡"}
        </button>
      </div>

      <div className="shop-card-body">
        <div className="shop-card-cat">{product.subCategory}</div>
        <div className="shop-card-name">{product.name}</div>
        <div className="shop-card-rating">
          <Stars rating={product.rating} />
          <span className="shop-rating-val">{product.rating}</span>
          <span className="shop-review-ct">({product.reviews.toLocaleString("en-IN")})</span>
        </div>
        <div className="shop-card-price">
          <span className="shop-price-now">{fmt(product.price)}</span>
          <span className="shop-price-mrp">{fmt(product.mrp)}</span>
          <span className="shop-price-pct">{pct}% off</span>
        </div>
        <div className="shop-price-save">Save {fmt(product.mrp - product.price)}</div>

        {product.inStock && (
          <>
            <div className="shop-card-delivery">
              {product.freeDelivery ? (
                <span className="shop-free-del">FREE delivery</span>
              ) : ("Delivery")}{" "}{product.delivery}
            </div>
            {product.pointsEligible && (
              <div className="shop-pts-buy-badge">
                <span className="shop-pts-buy-text">Buy with</span>
                <span className="shop-pts-buy-val">{product.pointsPrice.toLocaleString("en-IN")} pts</span>
              </div>
            )}
            <div className="shop-qty-wrap">
              <span className="shop-qty-label">Qty:</span>
              <div className="shop-qty-ctrl">
                <button className="shop-qty-btn" onClick={(e) => { e.stopPropagation(); setQty((q) => Math.max(1, q - 1)); }}>−</button>
                <div className="shop-qty-num">{qty}</div>
                <button className="shop-qty-btn" onClick={(e) => { e.stopPropagation(); setQty((q) => Math.min(10, q + 1)); }}>+</button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="shop-card-footer">
        <button
          className="shop-btn-glass-blue"
          disabled={!product.inStock}
          onClick={(e) => { e.stopPropagation(); onAddCart(product, qty); }}
        >
          {product.inStock ? "Add to Cart" : "Out of Stock"}
        </button>
        <button
          className="shop-btn-glass-yellow"
          onClick={(e) => { e.stopPropagation(); onViewDetail(product); }}
        >
          View Details
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   PRODUCT DETAIL
============================================================ */
function ProductDetailPage({ product, onBack, onAddCart, onWishlist, isWishlisted, onBuyNow, onBuyWithPoints, userPoints, isLoggedIn }) {
  const [qty, setQty] = useState(1);
  const [selectedColor, setSelectedColor] = useState(product.colors?.[0] || null);
  const pct = calcDiscount(product.mrp, product.price);

  return (
    <div className="shop-detail-page" data-aos="fade-up">
      <button className="shop-detail-back" onClick={onBack}>Back</button>
      <div className="shop-detail-grid" data-aos="fade-left" data-aos-delay="100">
        <div className="shop-detail-img-wrap">
          <img className="shop-detail-img" src={product.image} alt={product.name} />
        </div>
        <div className="shop-detail-info">
          <div className="shop-detail-cat">{product.subCategory}</div>
          <h1 className="shop-detail-name">{product.name}</h1>
          <div className="shop-detail-rating">
            <Stars rating={product.rating} />
            <span className="shop-rating-val">{product.rating}</span>
            <span className="shop-review-ct">{product.reviews.toLocaleString("en-IN")} ratings</span>
          </div>

          <div className="shop-detail-price-block">
            <div className="shop-detail-price-row">
              <span className="shop-detail-price-now">{fmt(product.price)}</span>
              <span className="shop-detail-price-mrp">{fmt(product.mrp)}</span>
              <span className="shop-detail-price-off">{pct}% off</span>
            </div>
            <div className="shop-detail-price-save">You save {fmt(product.mrp - product.price)}</div>
          </div>

          {product.pointsEligible && isLoggedIn && (
            <div className="shop-detail-pts-buy">
              <div className="shop-detail-pts-buy-info">
                <div className="shop-detail-pts-buy-title">Buy with Reward Points</div>
                <div className="shop-detail-pts-buy-sub">
                  Balance: {userPoints.toLocaleString("en-IN")} pts (1₹ = 1pt)
                  {userPoints < (product.pointsPrice * qty) / POINTS_RATE ? " (insufficient)" : " ✓ eligible"}
                </div>
              </div>
              <div className="shop-detail-pts-buy-val">{(product.pointsPrice * qty).toLocaleString("en-IN")} pts</div>
            </div>
          )}

          <div className="shop-detail-delivery">
            {product.inStock ? (
              <>
                {product.freeDelivery ? (
                  <span className="shop-free-del">FREE Delivery</span>
                ) : (
                  <span>Delivery {fmt(SHOP_CONFIG.deliveryCharge)}</span>
                )}{" "}— by {product.delivery}
              </>
            ) : (
              <span style={{ color: "var(--shop-red)", fontWeight: 700 }}>Currently Unavailable</span>
            )}
          </div>

          {product.features?.length > 0 && (
            <div className="shop-detail-features">
              <div className="shop-detail-features-title">Key Features</div>
              <div className="shop-detail-feature-chips">
                {product.features.map((f, i) => (
                  <span key={i} className="shop-detail-chip">{f}</span>
                ))}
              </div>
            </div>
          )}

          <p className="shop-detail-desc">{product.description}</p>

          {product.colors?.length > 0 && (
            <div>
              <div className="shop-detail-features-title" style={{ marginBottom: 7 }}>Available Colors</div>
              <div className="shop-detail-colors">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    className={`shop-detail-color-chip${selectedColor === c ? " shop-selected" : ""}`}
                    onClick={() => setSelectedColor(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.inStock && (
            <div className="shop-detail-qty-row">
              <span className="shop-detail-qty-label">Quantity:</span>
              <div className="shop-qty-ctrl">
                <button className="shop-qty-btn" onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button>
                <div className="shop-qty-num">{qty}</div>
                <button className="shop-qty-btn" onClick={() => setQty((q) => Math.min(10, q + 1))}>+</button>
              </div>
            </div>
          )}

          <div className="shop-detail-actions">
            <button className="shop-btn-glass-blue" disabled={!product.inStock} onClick={() => onAddCart(product, qty)}>
              Add to Cart
            </button>
            <button className="shop-btn-glass-yellow" disabled={!product.inStock} onClick={() => onBuyNow(product, qty)}>
              Buy Now
            </button>
          </div>

          {product.inStock && product.pointsEligible && isLoggedIn && (
            <button
              className="shop-btn-glass-green"
              style={{ padding: 12, fontSize: 13.5 }}
              onClick={() => onBuyWithPoints(product, qty)}
            >
              Buy with {(product.pointsPrice * qty).toLocaleString("en-IN")} Points
            </button>
          )}

          <button className="shop-btn-glass-blue" style={{ marginTop: -4 }} onClick={() => onWishlist(product.id)}>
            {isWishlisted ? "♥ Saved to Wishlist" : "♡ Save to Wishlist"}
          </button>

          <div className="shop-detail-meta">
            <div>Seller: <span>{product.seller}</span></div>
            <div>Warranty: <span>{product.warranty}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   CART SIDEBAR
============================================================ */
function CartSidebar({ cart, onClose, onQtyChange, onRemove, onCheckout }) {
  const subtotal = cart.reduce((s, it) => s + it.price * it.qty, 0);
  const totalMrp = cart.reduce((s, it) => s + it.mrp * it.qty, 0);
  const savings = totalMrp - subtotal;
  const delivery = subtotal >= SHOP_CONFIG.minFreeDelivery || subtotal === 0 ? 0 : SHOP_CONFIG.deliveryCharge;
  const tax = Math.round(subtotal * SHOP_CONFIG.taxRate);
  const total = subtotal + delivery + tax;

  return (
    <>
      <div className="shop-overlay" onClick={onClose} />
      <div className="shop-sidebar">
        <div className="shop-sidebar-head">
          <span className="shop-sidebar-title">Your Cart ({cart.length})</span>
          <button className="shop-sidebar-close" onClick={onClose}>✕</button>
        </div>
        <div className="shop-sidebar-body">
          {cart.length === 0 ? (
            <div className="shop-empty">
              <div className="shop-empty-icon">🛒</div>
              <div className="shop-empty-title">Your cart is empty</div>
              <div className="shop-empty-sub">Add products to get started</div>
            </div>
          ) : (
            cart.map((item) => (
              <div key={item.id} className="shop-cart-item">
                <img className="shop-ci-img" src={item.image} alt={item.shortName} />
                <div className="shop-ci-info">
                  <div className="shop-ci-name">{item.name}</div>
                  <div className="shop-ci-mrp">{fmt(item.mrp)} MRP</div>
                  <div className="shop-ci-price">{fmt(item.price)}</div>
                  <div className="shop-ci-row">
                    <div className="shop-qty-ctrl">
                      <button className="shop-qty-btn" onClick={() => onQtyChange(item.id, item.qty - 1)}>−</button>
                      <div className="shop-qty-num">{item.qty}</div>
                      <button className="shop-qty-btn" onClick={() => onQtyChange(item.id, item.qty + 1)}>+</button>
                    </div>
                    <button className="shop-ci-remove" onClick={() => onRemove(item.id)}>Remove</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {cart.length > 0 && (
          <div className="shop-sidebar-footer">
            <div className="shop-summary-rows">
              <div className="shop-sr"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
              <div className="shop-sr"><span>Savings</span><span style={{ color: "var(--shop-green)" }}>-{fmt(savings)}</span></div>
              <div className="shop-sr">
                <span>Delivery</span>
                <span>{delivery === 0 ? <span style={{ color: "var(--shop-green)", fontWeight: 600 }}>FREE</span> : fmt(delivery)}</span>
              </div>
              <div className="shop-sr"><span>GST ({Math.round(SHOP_CONFIG.taxRate * 100)}%)</span><span>{fmt(tax)}</span></div>
              <div className="shop-sr shop-sr-total"><span>Total</span><span>{fmt(total)}</span></div>
            </div>
            <button
              className="shop-btn-glass-yellow"
              style={{ padding: 13, fontSize: 14 }}
              onClick={() => onCheckout(subtotal, tax, delivery)}
            >
              Proceed to Checkout — {fmt(total)}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/* ============================================================
   WISHLIST SIDEBAR
============================================================ */
function WishlistSidebar({ wishlist, products, onClose, onAddCart, onRemove }) {
  const wishProducts = products.filter((p) => wishlist.includes(p.id));
  return (
    <>
      <div className="shop-overlay" onClick={onClose} />
      <div className="shop-sidebar">
        <div className="shop-sidebar-head">
          <span className="shop-sidebar-title">Wishlist ({wishProducts.length})</span>
          <button className="shop-sidebar-close" onClick={onClose}>✕</button>
        </div>
        <div className="shop-sidebar-body">
          {wishProducts.length === 0 ? (
            <div className="shop-empty">
              <div className="shop-empty-icon">♡</div>
              <div className="shop-empty-title">Wishlist is empty</div>
              <div className="shop-empty-sub">Save items you love</div>
            </div>
          ) : (
            <div className="shop-wish-grid">
              {wishProducts.map((p) => (
                <div key={p.id} className="shop-wish-item">
                  <img className="shop-wi-img" src={p.image} alt={p.shortName} />
                  <div className="shop-wi-body">
                    <div className="shop-wi-name">{p.name}</div>
                    <div className="shop-wi-price">{fmt(p.price)}</div>
                    <div className="shop-wi-mrp">{fmt(p.mrp)}</div>
                    {!p.inStock && <div className="shop-wi-oos">Out of Stock</div>}
                    <div className="shop-wi-actions">
                      <button
                        className="shop-btn-sm-blue"
                        disabled={!p.inStock}
                        onClick={() => { onAddCart(p, 1); onRemove(p.id); }}
                        style={{ flex: 1, fontSize: "10.5px", padding: "5px 6px" }}
                      >
                        {p.inStock ? "Add to Cart" : "Unavail."}
                      </button>
                      <button className="shop-btn-sm-outline" onClick={() => onRemove(p.id)}>✕</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ============================================================
   PAYMENT MODAL — with saved address selector, consent, tip
============================================================ */
function PaymentModal({ subtotal, tax: initialTax, delivery, cart, onClose, onSuccess, userPoints = 0, isLoggedIn = false, userEmail = "" }) {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState("debit");
  const [payDetails, setPayDetails] = useState(PAYMENT_DETAILS_DEFAULT);
  const [tip, setTip] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [processing, setProcessing] = useState(false);
  const [pointsInput, setPointsInput] = useState("");
  const [editDelivery, setEditDelivery] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  // Saved addresses
  const [savedAddresses, setSavedAddresses] = useState([]);
  useEffect(() => { if (isLoggedIn) fetchSavedAddresses(userEmail).then(setSavedAddresses).catch(() => {}); }, [isLoggedIn, userEmail]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [useNewAddress, setUseNewAddress] = useState(false);

  const [form, setForm] = useState(isLoggedIn ? {
    name: "", phone: "",
    email: userEmail || "", address: "",
    city: "", pin: "",
    cardNum: "", expiry: "", cvv: "", upiId: "", nameOnCard: "", bankName: "", accountNum: "", ifsc: ""
  } : { name: "", phone: "", email: "", address: "", city: "", pin: "", cardNum: "", expiry: "", cvv: "", upiId: "", nameOnCard: "", bankName: "", accountNum: "", ifsc: "" });

  const setPayF = (e) => setPayDetails(d => ({ ...d, [e.target.name]: e.target.value }));

  const setF = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const profileComplete = isLoggedIn && !!form.name && !!form.phone && !!form.address;

  // When a saved address is selected, populate form
  function handleSelectSavedAddr(addr) {
    setSelectedAddrId(addr.id);
    setForm((f) => ({
      ...f,
      name: addr.name,
      phone: addr.phone,
      email: addr.email || f.email,
      address: addr.address,
      city: addr.city || "",
      pin: addr.pin || "",
    }));
    setUseNewAddress(false);
  }

  const availablePts   = userPoints;
  const enteredPts     = Math.min(parseInt(pointsInput) || 0, availablePts);
  const ptsDiscount    = Math.min(enteredPts, subtotal * 0.3);
  const ptsUsed        = Math.round(ptsDiscount);
  const convenienceFee = CONVENIENCE_FEES[method] || 0;
  const tipAmount = customTip !== "" ? parseInt(customTip) || 0 : tip;
  const tax = Math.round((subtotal - ptsDiscount) * SHOP_CONFIG.taxRate);
  const total = subtotal - ptsDiscount + delivery + tax + convenienceFee + tipAmount;

  // Step 2 "Pay" button triggers consent modal first
  function handlePayClick() {
    const err = validatePaymentDetails(method, payDetails);
    if (err) { alert(err); return; }
    setShowConsent(true);
  }

  function handleConsentAgree() {
    setShowConsent(false);
    setProcessing(true);
    setTimeout(() => {
      const trackingId = genTrackingId();
      const order = {
        id: genOrderId(),
        trackingId,
        date: new Date().toISOString().split("T")[0],
        items: cart.map((it) => ({ productId: it.id, name: it.name, qty: it.qty, price: it.price })),
        subtotal,
        ptsDiscount: Math.round(ptsDiscount),
        ptsUsed,
        tax,
        total,
        delivery,
        convenienceFee,
        tip: tipAmount,
        status: "Confirmed",
        paymentMethod: SHOP_PAYMENT_METHODS.find((m) => m.id === method)?.label || method,
        address: [form.address, form.city, form.pin].filter(Boolean).join(", "),
        pointsEarned: 0,
      };
      onSuccess(order);
      setProcessing(false);
    }, 1600);
  }

  return (
    <>
      <div className="shop-modal-overlay">
        <div className="shop-modal">
          <div className="shop-modal-head">
            <span className="shop-modal-title">
              {step === 1 ? "Delivery Details" : "Payment"}
            </span>
            <button className="shop-modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="shop-modal-body">
            {/* Order Summary */}
            <div className="shop-order-summary-box">
              <div className="shop-osb-title">Order Summary</div>
              <div className="shop-summary-rows">
                {cart.map((it) => (
                  <div key={it.id} className="shop-sr">
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 230, fontSize: 11.5 }}>
                      {it.name} ×{it.qty}
                    </span>
                    <span style={{ fontWeight: 700, color: "var(--shop-text)" }}>{fmt(it.price * it.qty)}</span>
                  </div>
                ))}
                <div style={{ height: 1, background: "var(--shop-border)", margin: "5px 0" }} />
                <div className="shop-sr"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                {ptsUsed > 0 && (
                  <div className="shop-sr shop-sr-discount"><span>Points Discount ({ptsUsed} pts)</span><span>-{fmt(Math.round(ptsDiscount))}</span></div>
                )}
                <div className="shop-sr"><span>Delivery</span><span>{delivery === 0 ? "FREE" : fmt(delivery)}</span></div>
                <div className="shop-sr"><span>GST ({Math.round(SHOP_CONFIG.taxRate * 100)}%)</span><span>{fmt(tax)}</span></div>
                {step === 2 && (
                  <>
                    {convenienceFee > 0 && (
                      <div className="shop-sr">
                        <span>Convenience Fee ({SHOP_PAYMENT_METHODS.find(m => m.id === method)?.label})</span>
                        <span>{fmt(convenienceFee)}</span>
                      </div>
                    )}
                    {tipAmount > 0 && (
                      <div className="shop-sr" style={{ color: "var(--shop-green)" }}>
                        <span>Tip 🙏</span><span>{fmt(tipAmount)}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="shop-sr shop-sr-total"><span>Total</span><span>{fmt(total)}</span></div>
              </div>
            </div>

            {/* Step 1: Delivery */}
            {step === 1 && (
              <>
                {/* Saved addresses — only if logged in and has saved addresses */}
                {isLoggedIn && savedAddresses.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontFamily: "var(--shop-f-head)", fontSize: 12.5, fontWeight: 700, color: "var(--shop-heading)" }}>
                      📍 Deliver to a saved address
                    </div>
                    {savedAddresses.map((addr) => {
                      const ti = ADDRESS_TYPES.find((t) => t.id === addr.type) || ADDRESS_TYPES[2];
                      const isSelected = selectedAddrId === addr.id && !useNewAddress;
                      return (
                        <div
                          key={addr.id}
                          onClick={() => handleSelectSavedAddr(addr)}
                          style={{
                            border: `2px solid ${isSelected ? "var(--shop-heading)" : "var(--shop-border)"}`,
                            borderRadius: "var(--shop-r-md)",
                            padding: "10px 14px",
                            background: isSelected ? "var(--shop-navy-pale)" : "var(--shop-white)",
                            cursor: "pointer",
                            transition: "all var(--shop-t)",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                          }}
                        >
                          <input type="radio" checked={isSelected} onChange={() => handleSelectSavedAddr(addr)} style={{ accentColor: "var(--shop-heading)", marginTop: 3, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                              <span style={{ fontSize: 14 }}>{ti.icon}</span>
                              <span style={{ fontFamily: "var(--shop-f-head)", fontWeight: 700, fontSize: 12.5, color: "var(--shop-heading)", textTransform: "capitalize" }}>
                                {addr.type}{addr.label ? ` — ${addr.label}` : ""}
                              </span>
                            </div>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--shop-text)" }}>{addr.name} · {addr.phone}</div>
                            <div style={{ fontSize: 12, color: "var(--shop-text-mid)" }}>{[addr.address, addr.city, addr.pin].filter(Boolean).join(", ")}</div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Option to use new / profile address */}
                    <div
                      onClick={() => { setSelectedAddrId(null); setUseNewAddress(true); }}
                      style={{
                        border: `2px solid ${useNewAddress || (!selectedAddrId && !profileComplete) ? "var(--shop-heading)" : "var(--shop-border)"}`,
                        borderRadius: "var(--shop-r-md)",
                        padding: "10px 14px",
                        background: useNewAddress ? "var(--shop-navy-pale)" : "var(--shop-white)",
                        cursor: "pointer",
                        transition: "all var(--shop-t)",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--shop-text-mid)",
                      }}
                    >
                      <input type="radio" checked={useNewAddress} onChange={() => { setSelectedAddrId(null); setUseNewAddress(true); }} style={{ accentColor: "var(--shop-heading)", flexShrink: 0 }} />
                      ➕ Use a different / new address
                    </div>
                  </div>
                )}

                {/* Show manual form if: no saved addresses, OR useNewAddress, OR not logged in */}
                {(!isLoggedIn || savedAddresses.length === 0 || useNewAddress) && (
                  <>
                    {profileComplete && !editDelivery && !useNewAddress ? (
                      <div className="shop-autofill-card">
                        <div className="shop-autofill-badge">Auto-filled from your profile</div>
                        <div className="shop-autofill-rows">
                          <div className="shop-autofill-row"><span>Name</span><strong>{form.name}</strong></div>
                          <div className="shop-autofill-row"><span>Phone</span><strong>{form.phone}</strong></div>
                          <div className="shop-autofill-row"><span>Email</span><strong>{form.email}</strong></div>
                          <div className="shop-autofill-row"><span>Address</span><strong>{[form.address, form.city, form.pin].filter(Boolean).join(", ")}</strong></div>
                        </div>
                        <button className="shop-autofill-edit-btn" onClick={() => setEditDelivery(true)}>Edit Details</button>
                      </div>
                    ) : (
                      <>
                        {isLoggedIn && (
                          <div className="shop-autofill-notice">Pre-filled from profile — edit if needed</div>
                        )}
                        <div className="shop-input-group">
                          <label className="shop-input-label">Full Name *</label>
                          <input className="shop-input" name="name" value={form.name} onChange={setF} placeholder="Your full name" />
                        </div>
                        <div className="shop-input-row">
                          <div className="shop-input-group">
                            <label className="shop-input-label">Phone *</label>
                            <input className="shop-input" name="phone" value={form.phone} onChange={setF} placeholder="+91 XXXXX XXXXX" />
                          </div>
                          <div className="shop-input-group">
                            <label className="shop-input-label">Email (optional)</label>
                            <input className="shop-input" name="email" value={form.email} onChange={setF} placeholder="you@email.com" />
                          </div>
                        </div>
                        <div className="shop-input-group">
                          <label className="shop-input-label">Delivery Address *</label>
                          <input className="shop-input" name="address" value={form.address} onChange={setF} placeholder="House/Flat no, Street, Area" />
                        </div>
                        <div className="shop-input-row">
                          <div className="shop-input-group">
                            <label className="shop-input-label">City</label>
                            <input className="shop-input" name="city" value={form.city} onChange={setF} placeholder="City" />
                          </div>
                          <div className="shop-input-group">
                            <label className="shop-input-label">PIN Code</label>
                            <input className="shop-input" name="pin" value={form.pin} onChange={setF} placeholder="110001" />
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                <button className="shop-btn-glass-blue" style={{ padding: 13, fontSize: 14 }} onClick={() => setStep(2)}>
                  Continue to Payment 
                </button>
              </>
            )}

            {/* Step 2: Payment */}
            {step === 2 && (
              <>
                <PaymentForm
                  selectedMethod={method}
                  onMethodSelect={(id) => { setMethod(id); setPayDetails(PAYMENT_DETAILS_DEFAULT); }}
                  details={payDetails}
                  onDetailChange={setPayF}
                />

                {/* Points discount */}
                {isLoggedIn && availablePts > 0 && (
                  <div className="shop-pts-discount-box">
                    <div className="shop-pts-discount-label">Use Reward Points for Discount</div>
                    <div className="shop-pts-discount-sub">Available: {availablePts.toLocaleString("en-IN")} pts (max 30% of subtotal)</div>
                    <div className="shop-pts-discount-row">
                      <input
                        className="shop-input"
                        type="number"
                        min={0}
                        max={availablePts}
                        value={pointsInput}
                        onChange={(e) => setPointsInput(e.target.value)}
                        placeholder={`Enter points (max ${availablePts})`}
                      />
                      <button className="shop-pts-apply-btn" onClick={() => setPointsInput(String(Math.min(availablePts, Math.round(subtotal * 0.3 * POINTS_RATE))))}>
                        Max
                      </button>
                      {pointsInput && <button className="shop-pts-apply-btn" style={{ background: "#f3f4f6", color: "var(--shop-text)" }} onClick={() => setPointsInput("")}>Clear</button>}
                    </div>
                    {ptsUsed > 0 && (
                      <div className="shop-pts-discount-saving">Saving {fmt(Math.round(ptsDiscount))} with {ptsUsed} pts</div>
                    )}
                  </div>
                )}

                {/* Tip */}
                <div className="shop-tip-section">
                  <div className="shop-tip-title">Add a Tip for Our Delivery Partner</div>
                  <div className="shop-tip-sub">100% goes to your delivery partner</div>
                  <div className="shop-tip-presets">
                    {TIP_PRESETS.map((t) => (
                      <button
                        key={t}
                        className={`shop-tip-btn${tip === t && customTip === "" ? " shop-active" : ""}`}
                        onClick={() => { setTip(t); setCustomTip(""); }}
                      >
                        {t === 0 ? "No Tip" : `₹${t}`}
                      </button>
                    ))}
                  </div>
                  <div className="shop-input-group" style={{ marginTop: 8 }}>
                    <label className="shop-input-label">Custom Tip Amount (₹)</label>
                    <input
                      className="shop-input"
                      type="number"
                      min={0}
                      value={customTip}
                      onChange={(e) => { setCustomTip(e.target.value); setTip(0); }}
                      placeholder="Enter custom amount"
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                  <button className="shop-btn-sm-outline" style={{ flex: 1, padding: 12 }} onClick={() => setStep(1)}>← Back</button>
                  <button
                    className="shop-btn-glass-yellow"
                    style={{ flex: 2, padding: 13, fontSize: 14 }}
                    onClick={handlePayClick}
                    disabled={processing}
                  >
                    {processing ? "Processing..." : `Pay ${fmt(total)}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Consent Modal — rendered on top */}
      {showConsent && (
        <ConsentModal
          onAgree={handleConsentAgree}
          onClose={() => setShowConsent(false)}
        />
      )}
    </>
  );
}

/* ============================================================
   SUCCESS MODAL — with tracking ID
============================================================ */
function SuccessModal({ order, onClose }) {
  return (
    <div className="shop-modal-overlay">
      <div className="shop-modal">
        <div className="shop-modal-head">
          <span className="shop-modal-title">Order Confirmed ✓</span>
          <button className="shop-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="shop-modal-body">
          <div className="shop-success">
            <div className="shop-success-icon">✓</div>
            <div className="shop-success-title">Payment Successful!</div>
            <p className="shop-success-sub">
              Your order has been placed.<br />Expected delivery in 2–5 business days.
            </p>
            <div className="shop-success-ordid">Order ID: {order.id}</div>
            <div className="shop-success-tracking">
              <span>Tracking ID:</span>
              <strong>{order.trackingId}</strong>
            </div>
            <div className="shop-success-total">
              Total Paid: <strong>{fmt(order.total)}</strong> via {order.paymentMethod}
            </div>
            {order.tip > 0 && (
              <div style={{ fontSize: 12, color: "var(--shop-green)", fontWeight: 600 }}>
                Thanks for the ₹{order.tip} tip to your delivery partner!
              </div>
            )}
            <button
              className="shop-btn-glass-blue"
              style={{ padding: "11px 28px", width: "auto" }}
              onClick={() => downloadReceipt(order)}
            >
              Download Receipt
            </button>
            <button className="shop-continue-link" onClick={onClose}>Continue Shopping</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   POINTS SUCCESS MODAL
============================================================ */
function PointsSuccessModal({ product, qty, pointsUsed, order, onClose }) {
  return (
    <div className="shop-modal-overlay">
      <div className="shop-modal">
        <div className="shop-modal-head">
          <span className="shop-modal-title">Points Order Confirmed</span>
          <button className="shop-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="shop-modal-body">
          <div className="shop-success">
            <div className="shop-success-icon">✓</div>
            <div className="shop-success-title">Order Placed with Points!</div>
            <p className="shop-success-sub">
              {product.shortName} ×{qty}<br />Delivery in 2–5 business days.
            </p>
            <div className="shop-success-ordid">Order ID: {order?.id || "—"}</div>
            <div className="shop-success-tracking">
              <span>Tracking ID:</span>
              <strong>{order?.trackingId || "—"}</strong>
            </div>
            <div className="shop-success-pts">−{pointsUsed.toLocaleString("en-IN")} pts ≈ ₹{(pointsUsed / POINTS_RATE).toLocaleString("en-IN", { maximumFractionDigits: 2 })} deducted</div>
            {order && (
              <button
                className="shop-btn-glass-blue"
                style={{ padding: "9px 22px", width: "auto" }}
                onClick={() => downloadReceipt(order)}
              >
                Download Receipt
              </button>
            )}
            <button className="shop-continue-link" onClick={onClose}>Continue Shopping</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SHOP ORDERS — full page
============================================================ */
function ShopOrdersPage({ orders, onBack, onReorder, isLoggedIn, onLoginClick }) {
  const [histTab, setHistTab] = useState("all");
  const filtered = histTab === "all" ? orders
    : histTab === "points" ? orders.filter((o) => o.paymentMethod === "Points")
    : orders.filter((o) => o.paymentMethod !== "Points");
  const cashCount = orders.filter((o) => o.paymentMethod !== "Points").length;
  const ptsCount  = orders.filter((o) => o.paymentMethod === "Points").length;

  if (!isLoggedIn) {
    return (
      <div className="shop-fullpage">
        <div className="shop-fullpage-head">
          <button className="shop-back-btn" onClick={onBack}>Back to Shop</button>
          <div />
        </div>
        <div className="shop-orders-page">
          <div className="shop-empty">
            <div className="shop-empty-icon">📦</div>
            <div className="shop-empty-title">Sign in to view your orders</div>
            <button className="shop-btn-solid" style={{ marginTop: 16, width: "auto", padding: "10px 28px" }} onClick={onLoginClick}>Sign In / Sign Up</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-fullpage">
      <div className="shop-fullpage-head">
        <button className="shop-back-btn" onClick={onBack}>Back to Shop</button>
        <div />
      </div>
      <div className="shop-orders-page">
        <div className="shop-hist-tabs">
          {[["all", `All (${orders.length})`], ["cash", `Cash (${cashCount})`], ["points", `⭐ Points (${ptsCount})`]].map(([k, lbl]) => (
            <button key={k} className={`shop-hist-tab${histTab === k ? " shop-active" : ""}`} onClick={() => setHistTab(k)}>{lbl}</button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="shop-empty">
            <div className="shop-empty-icon">📦</div>
            <div className="shop-empty-title">{orders.length === 0 ? "No orders yet — start shopping!" : "No orders in this category"}</div>
          </div>
        ) : (
          <div className="shop-orders-grid">
            {filtered.map((order) => {
              const isPoints = order.paymentMethod === "Points";
              return (
                <div key={order.id} className={`shop-history-card${isPoints ? " shop-hc-pts" : ""}`}>
                  <div className="shop-hc-head">
                    <div>
                      <div className="shop-hc-id">{order.id}</div>
                      <div className="shop-hc-date">
                        {order.date}&nbsp;&nbsp;
                        <span className={`shop-hc-method-badge${isPoints ? " shop-pts" : " shop-cash"}`}>{order.paymentMethod}</span>
                      </div>
                      {order.trackingId && <div className="shop-hc-tracking"> {order.trackingId}</div>}
                    </div>
                    <span className="shop-hc-status">{order.status}</span>
                  </div>
                  <div className="shop-hc-body">
                    {(order.items || []).map((it, i) => (
                      <div key={i} className="shop-hc-item">
                        <span className="shop-hc-item-name">{it.name}</span>
                        <span className="shop-hc-item-price">{isPoints ? "—" : fmt(it.price * it.qty)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="shop-hc-foot">
                    <div>
                      {isPoints
                        ? <div className="shop-hc-total">Total: {fmt(order.total || (order.pointsUsed || 0) / POINTS_RATE)}</div>
                        : <div className="shop-hc-total">Total: {fmt(order.total)}</div>
                      }
                    </div>
                    <div className="shop-hc-actions">
                      <button className="shop-btn-sm-blue" onClick={() => downloadReceipt(order)}>Receipt PDF</button>
                      <button className="shop-btn-sm-yellow" onClick={() => onReorder(order)}>Reorder</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   MAIN APP
============================================================ */
export default function ShoppingMart() {
  const { user: authUser, logout: authLogout } = useAuth();
  const [activeCat, setActiveCat] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);

  // Fetch products from backend API on mount
  useEffect(() => {
    shoppingService.listProducts().then((r) => {
      const apiProducts = r?.data;
      if (apiProducts && Array.isArray(apiProducts)) {
        setProducts(apiProducts);
      }
    }).catch(() => {});
  }, []);

  // Auth
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);

  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [orders, setOrders] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const [redeemableBalance, setRedeemableBalance] = useState(0);
  const [hasEarnedRe, setHasEarnedRe] = useState(false);

  const [showCart, setShowCart] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);

  const [detailProduct, setDetailProduct] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [successOrder, setSuccessOrder] = useState(null);
  const [buyWithPointsData, setBuyWithPointsData] = useState(null);
  const [pointsSuccess, setPointsSuccess] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg) => setToast(msg), []);

  const loadUserOrders = useCallback((email) => {
    const stored = getShopOrders(email);
    const combined = [...stored, ...SHOP_INITIAL_ORDERS];
    const seen = new Set();
    return combined.filter((o) => { if (seen.has(o.id)) return false; seen.add(o.id); return true; });
  }, []);

  useEffect(() => {
    const session = authUser;
    if (session) {
      setIsLoggedIn(true);
      setUser(session);
      setOrders(loadUserOrders(session.email));
      getTotalWalletBalance(session.email).then(b => setUserPoints(b)).catch(() => {});
      getRedeemableBalance(session.email).then(b => setRedeemableBalance(b)).catch(() => {});
      hasEarnedRedeemable(session.email).then(b => setHasEarnedRe(b)).catch(() => {});
    }
  }, [authUser, loadUserOrders]);

  const handleLogin = useCallback((userData) => {
    setIsLoggedIn(true);
    setUser(userData);
    setOrders(loadUserOrders(userData.email));
    showToast(`Welcome back, ${userData.name}!`);
    getTotalWalletBalance(userData.email).then(b => setUserPoints(b)).catch(() => {});
    getRedeemableBalance(userData.email).then(b => setRedeemableBalance(b)).catch(() => {});
    hasEarnedRedeemable(userData.email).then(b => setHasEarnedRe(b)).catch(() => {});
  }, [showToast, loadUserOrders]);

  const handleLogout = useCallback(() => {
    authLogout();
    setIsLoggedIn(false);
    setUser(null);
    setUserPoints(0);
    setOrders([]);
    setShowHistory(false);
    setShowUserDetails(false);
    showToast("Logged out successfully");
  }, [showToast, authLogout]);

  const handleAddCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const ex = prev.find((it) => it.id === product.id);
      if (ex) return prev.map((it) => it.id === product.id ? { ...it, qty: Math.min(it.qty + qty, 10) } : it);
      return [...prev, { ...product, qty }];
    });
    showToast(`"${product.shortName}" added to cart`);
    setShowCart(true);
  }, [showToast]);

  const handleBuyNow = useCallback((product, qty = 1) => {
    const sub = product.price * qty;
    const del = sub >= SHOP_CONFIG.minFreeDelivery ? 0 : SHOP_CONFIG.deliveryCharge;
    const tax = Math.round(sub * SHOP_CONFIG.taxRate);
    setShowCart(false);
    setDetailProduct(null);
    setPaymentData({ subtotal: sub, tax, delivery: del, cart: [{ ...product, qty }] });
  }, []);

  const handleBuyWithPoints = useCallback((product, qty = 1) => {
    if (!isLoggedIn) { setShowLogin(true); showToast("Please login to use points"); return; }
    setBuyWithPointsData({ product, qty });
  }, [isLoggedIn, showToast]);

  const handlePointsPurchaseConfirm = useCallback((pointsUsed) => {
    const { product, qty } = buyWithPointsData;
    setUserPoints((prev) => Math.max(0, prev - pointsUsed));
    const ptOrder = {
      id: genOrderId(),
      trackingId: genTrackingId(),
      date: new Date().toISOString().split("T")[0],
      items: [{ productId: product.id, name: product.name, qty, price: 0 }],
      subtotal: 0, tax: 0, total: 0, delivery: 0, convenienceFee: 0, tip: 0,
      status: "Confirmed",
      paymentMethod: "Points",
      pointsUsed,
      address: "",
    };
    addShopOrder(user?.email, ptOrder);
    setOrders((prev) => [ptOrder, ...prev]);
    setBuyWithPointsData(null);
    setDetailProduct(null);
    setPointsSuccess({ product, qty, pointsUsed, order: ptOrder });
    showToast(`"${product.shortName}" ordered with ${pointsUsed.toLocaleString("en-IN")} points!`);
  }, [buyWithPointsData, showToast, user]);

  const handleCartQty = useCallback((id, qty) => {
    if (qty < 1) setCart((prev) => prev.filter((it) => it.id !== id));
    else setCart((prev) => prev.map((it) => it.id === id ? { ...it, qty: Math.min(qty, 10) } : it));
  }, []);

  const handleRemoveCart = useCallback((id) => setCart((prev) => prev.filter((it) => it.id !== id)), []);

  const handleWishlist = useCallback((id) => {
    setWishlist((prev) => {
      if (prev.includes(id)) { showToast("Removed from wishlist"); return prev.filter((w) => w !== id); }
      showToast("Saved to wishlist");
      return [...prev, id];
    });
  }, [showToast]);

  const handleCheckout = useCallback((subtotal, tax, delivery) => {
    setShowCart(false);
    setPaymentData({ subtotal, tax, delivery, cart });
  }, [cart]);

  const handlePaySuccess = useCallback((order) => {
    if (order.ptsUsed > 0 && user?.email) {
      setUserPoints((prev) => Math.max(0, prev - order.ptsUsed));
    }
    addShopOrder(user?.email, order);
    setOrders((prev) => [order, ...prev]);
    setCart([]);
    setPaymentData(null);
    setSuccessOrder(order);
  }, [user]);

  const handleReorder = useCallback((order) => {
    const toAdd = order.items.map((it) => {
      const p = products.find((pr) => pr.id === it.productId);
      return p && p.inStock ? { ...p, qty: it.qty } : null;
    }).filter(Boolean);
    if (!toAdd.length) { showToast("No in-stock items to reorder"); return; }
    setCart(toAdd);
    setShowHistory(false);
    setShowCart(true);
    showToast("Items added to cart!");
  }, [showToast]);

  const cartCount = cart.reduce((s, it) => s + it.qty, 0);

  const filteredProducts = (() => {
    let list = products;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.subCategory.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    }
    if (activeCat !== "all") list = list.filter((p) => p.category === activeCat);
    if (activeFilter === "popular") list = list.filter((p) => p.popular);
    if (activeFilter === "new") list = list.filter((p) => p.isNew);
    if (activeFilter === "inStock") list = list.filter((p) => p.inStock);
    if (activeFilter === "discount") list = [...list].sort((a, b) => calcDiscount(b.mrp, b.price) - calcDiscount(a.mrp, a.price));
    if (activeFilter === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    if (activeFilter === "priceLow") list = [...list].sort((a, b) => a.price - b.price);
    if (activeFilter === "priceHigh") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  })();

  const sectionTitle = searchQuery.trim()
    ? `Results for "${searchQuery}"`
    : activeCat === "all"
    ? "All Products"
    : SHOP_CATEGORIES.find((c) => c.id === activeCat)?.label || "Products";

  /* ────── HEADER ────── */
  const header = (
    <div className="shop-page-header">
      <div className="shop-topbar" data-aos="fade-down">
        <div className="shop-topbar-inner">
          <div className="shop-search-bar">
            <input
              className="shop-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setSearchQuery("")}
              placeholder="Search products"
            />
            {searchQuery && (
              <button className="shop-search-clear" onClick={() => setSearchQuery("")}>✕</button>
            )}
            <button className="shop-search-submit">Search</button>
          </div>

          <div className="shop-topbar-right">
            {isLoggedIn ? (
              <div className="shop-user-card">
                <div className="shop-points-pill" title="Total points balance">
                  <strong>{userPoints.toLocaleString("en-IN")} pts</strong>
                </div>
                <div className="shop-pts-rate-badge">1 Rupee = 1 Point</div>
                <button className="shop-details-btn" onClick={() => { setShowUserDetails(true); setShowHistory(false); setShowCart(false); setShowWishlist(false); }} title="My Details">
                  Profile
                </button>
                <button className="shop-user-btn" onClick={handleLogout} title={`Sign out of ${user?.email}`}>
                  Sign Out
                </button>
              </div>
            ) : (
              <button className="shop-login-btn" onClick={() => setShowLogin(true)} title="Sign In">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v1h20v-1c0-3.33-6.67-5-10-5z"/></svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={`shop-navbar${mobileMenuOpen ? " shop-mobile-open" : ""}`} data-aos="fade-up" data-aos-delay="80">
        <div className="shop-navbar-inner">
          <div className="shop-mobile-controls">
            <div className="shop-filter-dropdown-wrap">
              <button className="shop-filter-dropdown-btn">
                📂 {SHOP_CATEGORIES.find(c => c.id === activeCat)?.label || "All"}
                <span className="shop-filter-chevron">▾</span>
              </button>
              <div className="shop-filter-dropdown-menu">
                {SHOP_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    className={`shop-filter-dropdown-item${activeCat === cat.id ? " shop-active" : ""}`}
                    onClick={() => { setActiveCat(cat.id); setSearchQuery(""); setDetailProduct(null); setMobileMenuOpen(false); }}
                  >
                    {cat.label}
                    {activeCat === cat.id && <span className="shop-filter-check">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="shop-filter-dropdown-wrap">
              <button className="shop-filter-dropdown-btn">
                🎛️ {SHOP_FILTERS.find(f => f.id === activeFilter)?.label || "Filter"}
                <span className="shop-filter-chevron">▾</span>
              </button>
              <div className="shop-filter-dropdown-menu">
                {SHOP_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    className={`shop-filter-dropdown-item${activeFilter === f.id ? " shop-active" : ""}`}
                    onClick={() => { setActiveFilter(f.id); setMobileMenuOpen(false); }}
                  >
                    {f.label}
                    {activeFilter === f.id && <span className="shop-filter-check">✓</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="shop-cat-scroll">
            {SHOP_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`shop-cat-tab${activeCat === cat.id ? " shop-active" : ""}`}
                onClick={() => { setActiveCat(cat.id); setSearchQuery(""); setDetailProduct(null); }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="shop-navbar-right">
            <span className="shop-results-count">{filteredProducts.length} items</span>

            <div className="shop-filter-dropdown-wrap shop-desktop-filter">
              <button className="shop-filter-dropdown-btn">
                <span>🎛️</span>
                <span>{SHOP_FILTERS.find(f => f.id === activeFilter)?.label || "Filter"}</span>
                <span className="shop-filter-chevron">▾</span>
              </button>
              <div className="shop-filter-dropdown-menu">
                {SHOP_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    className={`shop-filter-dropdown-item${activeFilter === f.id ? " shop-active" : ""}`}
                    onClick={() => setActiveFilter(f.id)}
                  >
                    {f.label}
                    {activeFilter === f.id && <span className="shop-filter-check">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="shop-nav-actions">
              <button className="shop-nav-action-btn" onClick={() => { setShowWishlist(true); setShowCart(false); setShowHistory(false); setShowUserDetails(false); setMobileMenuOpen(false); }}>
                <span className="shop-action-icon">
                  ♥{wishlist.length > 0 && <span className="shop-action-badge">{wishlist.length}</span>}
                </span>
                <span>Wishlist</span>
              </button>
              <button className="shop-nav-action-btn" onClick={() => { setShowHistory(true); setShowCart(false); setShowWishlist(false); setShowUserDetails(false); setMobileMenuOpen(false); }}>
                <span className="shop-action-icon">📦</span>
                <span>Orders</span>
              </button>
              <button className="shop-nav-action-btn" onClick={() => { setShowCart(true); setShowWishlist(false); setShowHistory(false); setShowUserDetails(false); setMobileMenuOpen(false); }}>
                <span className="shop-action-icon">
                  🛒{cartCount > 0 && <span className="shop-action-badge">{cartCount}</span>}
                </span>
                <span>Cart</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const sharedModals = (
    <>
      {showLogin && (
        <AuthModal onClose={() => setShowLogin(false)} onLogin={handleLogin} />
      )}
      {showCart && (
        <CartSidebar
          cart={cart}
          onClose={() => setShowCart(false)}
          onQtyChange={handleCartQty}
          onRemove={handleRemoveCart}
          onCheckout={handleCheckout}
          userPoints={userPoints}
          isLoggedIn={isLoggedIn}
        />
      )}
      {showWishlist && (
        <WishlistSidebar
          wishlist={wishlist}
          products={products}
          onClose={() => setShowWishlist(false)}
          onAddCart={handleAddCart}
          onRemove={(id) => setWishlist((w) => w.filter((x) => x !== id))}
        />
      )}
      {buyWithPointsData && (
        <BuyWithPointsModal
          product={buyWithPointsData.product}
          qty={buyWithPointsData.qty}
          userPoints={userPoints}
          onClose={() => setBuyWithPointsData(null)}
          onConfirm={handlePointsPurchaseConfirm}
        />
      )}
      {paymentData && !successOrder && (
        <PaymentModal {...paymentData} onClose={() => setPaymentData(null)} onSuccess={handlePaySuccess} userPoints={userPoints} isLoggedIn={isLoggedIn} userEmail={user?.email || ""} />
      )}
      {successOrder && (
        <SuccessModal order={successOrder} onClose={() => setSuccessOrder(null)} />
      )}
      {pointsSuccess && (
        <PointsSuccessModal
          product={pointsSuccess.product}
          qty={pointsSuccess.qty}
          pointsUsed={pointsSuccess.pointsUsed}
          order={pointsSuccess.order}
          onClose={() => setPointsSuccess(null)}
        />
      )}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </>
  );

  if (showHistory) {
    return (
      <div className="shop-app">
        {header}
        <Ticker />
        <main className="shop-main">
          <ShopOrdersPage
            orders={orders}
            onBack={() => setShowHistory(false)}
            onReorder={handleReorder}
            isLoggedIn={isLoggedIn}
            onLoginClick={() => { setShowHistory(false); setShowLogin(true); }}
          />
        </main>
        {showLogin && <AuthModal onClose={() => setShowLogin(false)} onLogin={handleLogin} />}
        {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      </div>
    );
  }

  if (showUserDetails && isLoggedIn) {
    return (
      <div className="shop-app">
        {header}
        <Ticker />
        <main className="shop-main">
          <ShopProfilePage
            user={user}
            userPoints={userPoints}
            redeemableBalance={redeemableBalance}
            hasEarnedRedeemable={hasEarnedRe}
            onBack={() => setShowUserDetails(false)}
            onLogout={() => { handleLogout(); setShowUserDetails(false); }}
          />
        </main>
        {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      </div>
    );
  }

  if (detailProduct) {
    return (
      <div className="shop-app">
        {header}
        <Ticker />
        <main className="shop-main">
          <ProductDetailPage
            product={detailProduct}
            onBack={() => setDetailProduct(null)}
            onAddCart={handleAddCart}
            onWishlist={handleWishlist}
            isWishlisted={wishlist.includes(detailProduct.id)}
            onBuyNow={handleBuyNow}
            onBuyWithPoints={handleBuyWithPoints}
            userPoints={userPoints}
            isLoggedIn={isLoggedIn}
          />
        </main>
        {sharedModals}
      </div>
    );
  }

  return (
    <div className="shop-app">
      {header}
      <Ticker />
      <main className="shop-main">
        <div className="shop-container" data-aos="fade-up">
          <div className="shop-section-head" data-aos="fade-right">
            <span className="shop-section-title">{sectionTitle}</span>
            <span className="shop-section-sub">{filteredProducts.length} products</span>
          </div>
          {filteredProducts.length === 0 ? (
            <div className="shop-no-results" data-aos="fade-up">
              <div className="shop-no-results-icon">🔍</div>
              <div className="shop-no-results-title">No products found</div>
              <div className="shop-no-results-sub">Try adjusting your search or filters</div>
            </div>
          ) : (
            <div className="shop-grid" data-aos="fade-up" data-aos-delay="100">
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onAddCart={handleAddCart}
                  onViewDetail={setDetailProduct}
                  onWishlist={handleWishlist}
                  isWishlisted={wishlist.includes(product.id)}
                  onBuyWithPoints={handleBuyWithPoints}
                  userPoints={userPoints}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      {sharedModals}
    </div>
  );
}
